// ---------------------------------------------------------------------------
// Client-side dataset filtering.
//
// The Data Hub `datasets/search` API `query` field performs tokenized
// full-word matching, so partial substrings (e.g. "te") never match "test".
// To provide industry-standard substring search we fetch the full
// ownership-scoped set from the server and filter by name / access / type
// here. All three predicates compose with AND, so Access and Type always
// narrow the substring-filtered subset (and vice-versa).
// ---------------------------------------------------------------------------

import type {
  DatasetAccessFilter,
  DatasetDataType,
} from '../../constants/index.js';
import type { Dataset } from '../../services/dataHubService.interface.js';

const ACCESS_FILTER_ALL: DatasetAccessFilter = 'All';
const DATA_TYPE_FILTER_ALL: DatasetDataType = 'All types';

/** Case-insensitive substring match on the dataset name. */
export function matchesQuery(dataset: Dataset, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (needle === '') return true;
  return dataset.name.toLowerCase().includes(needle);
}

/** Exact access-level match, or pass-through when the filter is 'All'. */
export function matchesAccess(
  dataset: Dataset,
  accessFilter: DatasetAccessFilter,
): boolean {
  if (accessFilter === ACCESS_FILTER_ALL) return true;
  return dataset.accessLevel === accessFilter;
}

/** Exact data-type match, or pass-through when the filter is 'All types'. */
export function matchesDataType(
  dataset: Dataset,
  dataType: DatasetDataType,
): boolean {
  if (dataType === DATA_TYPE_FILTER_ALL) return true;
  return dataset.dataType === dataType;
}

/**
 * Applies the search query, access, and data-type filters with AND semantics.
 * Order is irrelevant to the result — every predicate must pass.
 */
export function filterDatasets(
  datasets: Dataset[],
  query: string,
  accessFilter: DatasetAccessFilter,
  dataType: DatasetDataType,
): Dataset[] {
  return datasets.filter(
    (dataset) =>
      matchesQuery(dataset, query) &&
      matchesAccess(dataset, accessFilter) &&
      matchesDataType(dataset, dataType),
  );
}
